import { getAppDbClient } from "~/lib/ssr/runtime";
import {
  BasicIndex,
  DbClient,
  collectionOptions,
  localOnlyCollectionOptions,
} from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import {
  parseSubsetFilters,
  toScopedCollectionId,
} from "~/lib/db/collection-helpers";
import { deferSubsetUnloadsUntilLoadsSettle } from "~/lib/db/deferred-subset-unload";
import {
  DEFAULT_SESSION_QUERY_SCOPE_TOKEN,
  queryKeys,
  withSessionQueryScope,
} from "~/lib/query-keys";
import {
  normalizeAssetBasicMarketDataBatchResponse,
  normalizeAssetTokenMetadataBatchResponse,
} from "../asset-domain-contract";
import type {
  AssetEquityDetailsModel,
  AssetModel,
  AssetPriceModel,
  AssetProviderExtensionModel,
  AssetTaxonomyModel,
  AssetTokenizedEquityValuationModel,
  AssetUniverseCanonicalModel,
  AssetUniverseGraphHydrationModel,
  AssetUniverseProductModel,
  AssetUniverseRouteModel,
  AssetUniverseSymbolLinkModel,
} from "./models";
import {
  cacheAssetDomainCollections,
  getAssetDomainCollectionId,
  getPublicCanonicalCollectionId,
  readCachedAssetDomainCollections,
  resetAssetDomainSessionState,
} from "./asset-domain-session-state";
import type {
  AssetDomainCollectionScope,
  AssetDomainDependencies,
  BrowserAssetDomainCollectionOptions,
} from "./collection-types";
import type { QueryBackedCollectionLike } from "./collection-sync";
import {
  chunkArray,
  extractAssetIds,
  getAssetCollectionMeta,
  preserveRequestedQueryRows,
} from "./collection-query-support";
import { getAssetDomainDependencies } from "./dependencies";

const ASSET_BATCH_LIMIT = 50;
const ASSET_METADATA_STALE_TIME_MS = 300_000;
const ASSET_PRICE_STALE_TIME_MS = 60_000;

type CollectionStateReader<T extends { id: string }> =
  | QueryBackedCollectionLike<T>
  | undefined;

/**
 * Fetch every chunk, keep whatever comes back.
 *
 * `Promise.all` here meant one rejected chunk discarded the successful ones
 * too, so a single unservable asset id blanked an entire portfolio's prices
 * rather than costing one row. Chunks are independent requests and the
 * response is keyed by asset id, so partial results are usable as-is: a
 * chunk that fails simply contributes no ids, and the caller's
 * `preserveRequestedQueryRows` keeps their cached rows.
 *
 * Only a total wipeout rethrows — that is a real outage (offline, auth,
 * server down) and the collection should surface it rather than quietly
 * report an empty world.
 */
async function fetchChunksTolerantly<Response>(
  assetIds: readonly string[],
  fetchChunk: (assetIdsChunk: string[]) => Promise<Response>,
): Promise<{ failedAssetIds: string[]; responses: Response[] }> {
  const chunks = chunkArray(assetIds, ASSET_BATCH_LIMIT);
  const settled = await Promise.allSettled(
    chunks.map((assetIdsChunk) => fetchChunk(assetIdsChunk)),
  );

  const responses: Response[] = [];
  const failedAssetIds: string[] = [];
  let firstRejection: unknown;

  settled.forEach((result, index) => {
    if (result.status === "fulfilled") {
      responses.push(result.value);
      return;
    }

    failedAssetIds.push(...(chunks[index] ?? []));
    firstRejection ??= result.reason;
  });

  if (responses.length === 0 && settled.length > 0) {
    throw firstRejection;
  }

  return { failedAssetIds, responses };
}

export function createAssetDomainCollections(
  scope: AssetDomainCollectionScope,
  options?: Partial<
    Pick<AssetDomainDependencies, "platformApi" | "queryClient">
  > & { dbClient?: DbClient },
) {
  const dbClient = options?.dbClient ?? new DbClient();
  const dependencies = getAssetDomainDependencies({
    platformApi: options?.platformApi,
    queryClient: options?.queryClient,
    sessionScopeToken: scope.sessionScopeToken,
  });
  const sessionScope = {
    scopeToken: dependencies.sessionScopeToken,
  };
  let assetRef: CollectionStateReader<AssetModel>;
  let assetPriceRef: CollectionStateReader<AssetPriceModel>;

  // Both on-demand collections defer subset unloads so a widening live query
  // (explore token stacks, symbol pages) never drops rows it is about to
  // re-own; see deferSubsetUnloadsUntilLoadsSettle for the flash it prevents.
  const assetCollectionId = getAssetDomainCollectionId(
    toScopedCollectionId("asset-domain-asset", scope.sessionScopeToken),
    scope.sessionScopeToken,
  );
  const asset = dbClient.collection(
    collectionOptions(assetCollectionId, () =>
      deferSubsetUnloadsUntilLoadsSettle(
        queryCollectionOptions({
          defaultIndexType: BasicIndex,
          id: assetCollectionId,
          getKey: (item: AssetModel) => item.id,
          queryClient: dependencies.queryClient,
          queryFn: async (context): Promise<AssetModel[]> => {
            const { filters } = parseSubsetFilters(
              getAssetCollectionMeta(context.meta),
            );
            const assetIds = extractAssetIds(filters);
            if (assetIds.length === 0) {
              return [];
            }

            // Deliberately NOT chunk-isolated: this endpoint already skips ids it
            // cannot parse server-side, and the delete/reconcile handshake below
            // depends on the exact microtask ordering an extra await would shift.
            const responses = await Promise.all(
              chunkArray(assetIds, ASSET_BATCH_LIMIT).map((assetIdsChunk) =>
                dependencies.platformApi.assets.getTokenMetadataBatch.query({
                  assetIds: assetIdsChunk,
                }),
              ),
            );

            const updatedAt = Date.now();
            const nextRows = responses.flatMap((response) =>
              normalizeAssetTokenMetadataBatchResponse({
                response,
                updatedAt,
              }),
            );

            // getTokenMetadataBatch routes through loadAssetOverlayCore on the
            // server, which already produces authoritative effective metadata
            // (base + custom overrides). When the server omits an asset from
            // the response it means that asset has no effective metadata
            // (e.g. its custom override was unpublished), so any cached row
            // for that requested id must be removed rather than shown stale.
            // We schedule the delete after the current write tick because the
            // queryCollectionOptions library reconciles queryFn output against
            // existing state on its own; explicit deletes interleaved with
            // that reconciliation get clobbered.
            const nextRowIds = new Set(nextRows.map((row) => row.id));
            const idsToDelete = assetIds.filter((id) => !nextRowIds.has(id));
            if (idsToDelete.length > 0 && assetRef) {
              const ref = assetRef;
              queueMicrotask(() => {
                const presentIds = idsToDelete.filter((id) =>
                  ref.state.has(id),
                );
                if (presentIds.length > 0) {
                  ref.utils.writeDelete(presentIds);
                }
              });
            }

            return preserveRequestedQueryRows({
              collectionState: assetRef?.state ?? new Map<string, AssetModel>(),
              nextRows,
              preserveMissing: false,
              requestedIds: assetIds,
            });
          },
          queryKey: withSessionQueryScope(
            queryKeys.assetDomain.assets,
            sessionScope,
          ),
          startSync: true,
          staleTime: ASSET_METADATA_STALE_TIME_MS,
          syncMode: "on-demand",
        }),
      ),
    ),
  );
  assetRef = asset;
  asset.createIndex((row) => row.assetId);

  const assetPriceCollectionId = getAssetDomainCollectionId(
    toScopedCollectionId("asset-domain-asset-price", scope.sessionScopeToken),
    scope.sessionScopeToken,
  );
  const assetPrice = dbClient.collection(
    collectionOptions(assetPriceCollectionId, () =>
      deferSubsetUnloadsUntilLoadsSettle(
        queryCollectionOptions({
          defaultIndexType: BasicIndex,
          id: assetPriceCollectionId,
          getKey: (item: AssetPriceModel) => item.id,
          queryClient: dependencies.queryClient,
          queryFn: async (context): Promise<AssetPriceModel[]> => {
            const { filters } = parseSubsetFilters(
              getAssetCollectionMeta(context.meta),
            );
            const assetIds = extractAssetIds(filters);
            if (assetIds.length === 0) {
              return [];
            }

            // Prices degrade per chunk: a failing chunk leaves its ids on their
            // cached rows (preserveRequestedQueryRows below) instead of taking the
            // whole portfolio's prices down with it.
            const { responses } = await fetchChunksTolerantly(
              assetIds,
              (assetIdsChunk) =>
                dependencies.platformApi.assets.getBasicMarketDataForAssets.query(
                  {
                    assetIds: assetIdsChunk,
                  },
                ),
            );

            const updatedAt = Date.now();
            return preserveRequestedQueryRows({
              collectionState:
                assetPriceRef?.state ?? new Map<string, AssetPriceModel>(),
              nextRows: responses.flatMap((response) =>
                normalizeAssetBasicMarketDataBatchResponse({
                  response,
                  updatedAt,
                }),
              ),
              requestedIds: assetIds,
            });
          },
          queryKey: withSessionQueryScope(
            queryKeys.assetDomain.assetPrices,
            sessionScope,
          ),
          refetchInterval: ASSET_PRICE_STALE_TIME_MS,
          startSync: true,
          staleTime: ASSET_PRICE_STALE_TIME_MS,
          syncMode: "on-demand",
        }),
      ),
    ),
  );
  assetPriceRef = assetPrice;
  assetPrice.createIndex((row) => row.assetId);

  const assetTaxonomy = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getAssetDomainCollectionId(
          toScopedCollectionId(
            "asset-domain-taxonomy",
            scope.sessionScopeToken,
          ),
          scope.sessionScopeToken,
        ),
        getKey: (item: AssetTaxonomyModel) => item.id,
      }),
    ),
  );
  assetTaxonomy.createIndex((row) => row.assetId);

  const assetEquityDetails = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getAssetDomainCollectionId(
          toScopedCollectionId(
            "asset-domain-equity-details",
            scope.sessionScopeToken,
          ),
          scope.sessionScopeToken,
        ),
        getKey: (item: AssetEquityDetailsModel) => item.id,
      }),
    ),
  );
  assetEquityDetails.createIndex((row) => row.assetId);

  const assetProviderExtensions = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getAssetDomainCollectionId(
          toScopedCollectionId(
            "asset-domain-provider-extensions",
            scope.sessionScopeToken,
          ),
          scope.sessionScopeToken,
        ),
        getKey: (item: AssetProviderExtensionModel) => item.id,
      }),
    ),
  );
  assetProviderExtensions.createIndex((row) => row.assetId);
  assetProviderExtensions.createIndex((row) => row.provider);

  const assetTokenizedEquityValuations = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getAssetDomainCollectionId(
          toScopedCollectionId(
            "asset-domain-tokenized-equity-valuations",
            scope.sessionScopeToken,
          ),
          scope.sessionScopeToken,
        ),
        getKey: (item: AssetTokenizedEquityValuationModel) => item.id,
      }),
    ),
  );
  assetTokenizedEquityValuations.createIndex((row) => row.assetId);
  assetTokenizedEquityValuations.createIndex((row) => row.canonicalId);

  // Canonical identity, metadata and quotes come from public asset/market
  // reads. DbClient interns this source across guest, user and VM scopes, so
  // SSR observers and their replacements keep the same rows through sign-in.
  // Routes, graph hydration and valuations below/above remain session scoped.
  const assetUniverseCanonical = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getPublicCanonicalCollectionId(),
        getKey: (item: AssetUniverseCanonicalModel) => item.id,
      }),
    ),
  );
  // Each scope reuses this collection; build its indexes only on first use.
  if (assetUniverseCanonical.indexes.size === 0) {
    assetUniverseCanonical.createIndex((row) => row.assetCanonicalId);
    assetUniverseCanonical.createIndex((row) => row.symbol);
  }

  const assetUniverseGraphHydration = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getAssetDomainCollectionId(
          toScopedCollectionId(
            "asset-domain-universe-graph-hydration",
            scope.sessionScopeToken,
          ),
          scope.sessionScopeToken,
        ),
        getKey: (item: AssetUniverseGraphHydrationModel) => item.id,
      }),
    ),
  );
  assetUniverseGraphHydration.createIndex((row) => row.assetCanonicalId);

  const assetUniverseProducts = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getAssetDomainCollectionId(
          toScopedCollectionId(
            "asset-domain-universe-products",
            scope.sessionScopeToken,
          ),
          scope.sessionScopeToken,
        ),
        getKey: (item: AssetUniverseProductModel) => item.id,
      }),
    ),
  );
  assetUniverseProducts.createIndex((row) => row.assetCanonicalId);
  assetUniverseProducts.createIndex((row) => row.productId);

  const assetUniverseRoutes = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getAssetDomainCollectionId(
          toScopedCollectionId(
            "asset-domain-universe-routes",
            scope.sessionScopeToken,
          ),
          scope.sessionScopeToken,
        ),
        getKey: (item: AssetUniverseRouteModel) => item.id,
      }),
    ),
  );
  assetUniverseRoutes.createIndex((row) => row.assetCanonicalId);
  assetUniverseRoutes.createIndex((row) => row.assetId);
  assetUniverseRoutes.createIndex((row) => row.productId);

  const assetUniverseSymbolLinks = dbClient.collection(
    collectionOptions(
      localOnlyCollectionOptions({
        defaultIndexType: BasicIndex,
        id: getAssetDomainCollectionId(
          toScopedCollectionId(
            "asset-domain-universe-symbol-links",
            scope.sessionScopeToken,
          ),
          scope.sessionScopeToken,
        ),
        getKey: (item: AssetUniverseSymbolLinkModel) => item.id,
      }),
    ),
  );
  assetUniverseSymbolLinks.createIndex((row) => row.assetCanonicalId);
  assetUniverseSymbolLinks.createIndex((row) => row.assetClass);
  assetUniverseSymbolLinks.createIndex((row) => row.symbol);

  return {
    asset,
    assetEquityDetails,
    assetPrice,
    assetProviderExtensions,
    assetTaxonomy,
    assetTokenizedEquityValuations,
    assetUniverseCanonical,
    assetUniverseGraphHydration,
    assetUniverseProducts,
    assetUniverseRoutes,
    assetUniverseSymbolLinks,
  };
}

export type AssetDomainCollections = ReturnType<
  typeof createAssetDomainCollections
>;

export function getAssetDomainCollections(
  options?: BrowserAssetDomainCollectionOptions,
): AssetDomainCollections {
  const sessionScopeToken =
    options?.sessionScopeToken ?? DEFAULT_SESSION_QUERY_SCOPE_TOKEN;
  const existing = readCachedAssetDomainCollections(sessionScopeToken);
  if (existing) {
    return existing;
  }

  const created = createAssetDomainCollections(
    { sessionScopeToken },
    {
      dbClient: getAppDbClient(),
      platformApi: options?.platformApi,
      queryClient: options?.queryClient,
    },
  );
  cacheAssetDomainCollections(sessionScopeToken, created);
  return created;
}

/**
 * Evict cached collection instances. Pass the scope token being retired —
 * clearing every scope would also evict instances the active scope created
 * during the current render, leaving mounted live queries bound to orphaned
 * instances that hydration can never reach.
 */
export function resetAssetDomainCollectionsCache(
  sessionScopeToken?: string,
): void {
  resetAssetDomainSessionState(sessionScopeToken);
}
